import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronDown, TriangleAlert } from "lucide-react"
import { useState } from "react"
import type { ApproveResult } from "../../../../../api/types"
import { AutoDismissDialog } from "../../../../shared/AutoDismissDialog"
import type { ApprovedSnapshot } from "./approvedSnapshot"
import {
  ApprovedVerdictMatrix,
  ApprovedVerdictSummary,
} from "./ApprovedVerdictMatrix"

export interface DeclarationApprovedDialogProps {
  result: ApproveResult
  /** Supplier as the studio names it (registry name, never an id). */
  supplierName: string
  /** Verordnungen as the navigator names them ("REACH, RoHS, POPS"). */
  frameworksLabel: string
  /** Countdown ran out or "Zur Übersicht": back to the list. */
  onDone: () => void
  /** The ✕ / Esc / backdrop route — closes the result and leaves the reviewer
   *  on the (now approved) declaration. Omitted: those fall back to `onDone`,
   *  the pre-existing behaviour. */
  onClose?: () => void
  /** Verdicts per Verordnung and article, taken from the studio state at
   *  approval time; feeds the collapsible "Urteile im Detail" section. */
  snapshot?: ApprovedSnapshot | null
  /** H2, the next `ready` declaration currently loaded
   *  (`nextOpenDeclaration`, `StudioV2Layout`'s own instance). `null`/
   *  undefined only means none is loaded right now, not that the reviewer's
   *  queue is empty (see that helper's own doc), the button simply doesn't
   *  render then, and "Zur Übersicht" stays the primary action. */
  nextDeclarationId?: string | null
  /** Navigates straight into `nextDeclarationId`, the same jump
   *  `StudioV2FooterBar`'s Skip and `goToNextOrDone` use. Only ever called
   *  when `nextDeclarationId` is set. */
  onNext?: () => void
}

const LOCALE: Record<string, string> = { DE: "de-DE", EN: "en-GB", JA: "ja-JP" }

/** The stagger the result's numbers arrive with, one step per stat (there are
 *  at most four). They fade in at their final value and never count up. */
const STAT_DELAYS = ["delay-0", "delay-75", "delay-150", "delay-225"]

/**
 * The success moment after Übernehmen, as a self-dismissing dialog over the
 * (now read-only) workbench instead of a full-page screen with a mandatory
 * click: the reviewer reads the result at a glance, the bar drains, the list
 * comes back. Names, never ids: supplier and Verordnung names in the
 * subtitle, no audit reference, no ISO timestamps.
 *
 * Holds (no countdown) when the approval went ahead over open work
 * (`nonBlockingWork`), the same rule the master-data summary applies to data
 * quality warnings: a caveat must not vanish unread.
 */
export function DeclarationApprovedDialog({
  result,
  supplierName,
  frameworksLabel,
  onDone,
  onClose,
  snapshot,
  nextDeclarationId,
  onNext,
}: DeclarationApprovedDialogProps) {
  const { translate, currentLanguage } = useTranslation()
  // Opening the detail matrix means the reviewer is reading: the countdown
  // stops for good, and stays stopped after the matrix is collapsed again
  // (`detailsEverOpened`), so nobody gets navigated away mid-thought
  // (Copilot, #2036).
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsEverOpened, setDetailsEverOpened] = useState(false)
  const handleDetailsOpenChange = (open: boolean) => {
    setDetailsOpen(open)
    if (open) setDetailsEverOpened(true)
  }
  const hasDetails =
    !!snapshot && snapshot.frameworks.length > 0 && snapshot.articles.length > 0
  const locale = LOCALE[String(currentLanguage).toUpperCase()] ?? "de-DE"

  const openWork = result.nonBlockingWork?.length ?? 0
  // Supplier on its own line, Verordnungen underneath (product-owner
  // screenshot, 28.08.: the " · "-joined single line read as one long name).
  const subtitle =
    supplierName || frameworksLabel ? (
      <>
        {supplierName && <span className="block">{supplierName}</span>}
        {frameworksLabel && <span className="block">{frameworksLabel}</span>}
      </>
    ) : undefined

  const stats: Array<{
    key: string
    label: string
    value: string
    /** F30 — set only when the value is a past date; rendered as a second,
     *  danger-toned line under the value. */
    qualifier?: string
  }> = [
    {
      key: "articles",
      label: translate("MC_DECL_V2_APPROVED_STAT_ARTICLES"),
      // Articles that got a verdict, as the confirm dialog's preview counts them, not
      // the approve scope (`articles`), which is the whole pool for a declaration
      // whose statements were narrowed to a subset (10582 shown for 2750 approved).
      value: String(result.articlesWithVerdict ?? result.articles),
    },
    {
      key: "verdicts",
      label: translate("MC_DECL_V2_APPROVED_STAT_VERDICTS"),
      value: String(result.statementWrites ?? 0),
    },
  ]
  if (result.substanceWrites > 0) {
    stats.push({
      key: "substances",
      label: translate("MC_DECL_V2_APPROVED_STAT_SUBSTANCES"),
      value: String(result.substanceWrites),
    })
  }
  // F30 — Task 5's default-validity write can leave freshness_date in the
  // past for an old document (declaration date + 180 days from an old
  // received_at). The date itself is never wrong, so it renders unchanged;
  // only a past date additionally gets the qualifier below.
  const freshnessDaysAgo = pastDaysAgo(result.freshnessDate)
  stats.push({
    key: "freshness",
    label: translate("MC_DECL_V2_APPROVED_STAT_FRESHNESS"),
    value: formatDate(result.freshnessDate, locale),
    qualifier:
      freshnessDaysAgo !== null
        ? translate("MC_DECL_RECORD_EXPIRED_AGO", { n: freshnessDaysAgo })
        : undefined,
  })

  // Separate from overwriteLine below: a manually-linked substance value
  // (source: 'manual', TUR-1465) that this approval's write replaced. Kept as
  // its own sentence rather than folded into overwriteLine, which is already
  // parameterized for exactly two counts.
  const manualOverwriteLine =
    result.manualOverwritten > 0
      ? translate("MC_DECL_V2_APPROVED_MANUAL_OVERWRITTEN", {
          n: result.manualOverwritten,
        })
      : null

  // "No newer values overwritten" must not render alongside manualOverwriteLine —
  // a manual link WAS overwritten, so claiming nothing was would contradict the
  // line right below it. When only a manual overwrite happened (no claim-level
  // overwrite/kept), skip this line entirely rather than assert a false negative.
  const overwriteLine =
    result.overwritten + result.kept > 0
      ? translate("MC_DECL_V2_APPROVED_OVERWRITTEN", {
          o: result.overwritten,
          k: result.kept,
        })
      : manualOverwriteLine
        ? null
        : translate("MC_DECL_V2_APPROVED_NO_OVERWRITE")

  // H2, "Nächste Erklärung" only takes over as the primary action once a
  // next `ready` declaration is actually loaded AND the caller wired a
  // handler for it; either missing keeps today's single-button footer
  // ("Zur Übersicht" primary), never a button that would do nothing.
  const showNext = !!nextDeclarationId && !!onNext

  return (
    <AutoDismissDialog
      title={translate("MC_DECL_V2_APPROVED_TITLE")}
      description={subtitle}
      hold={openWork > 0 || detailsEverOpened}
      onDone={onDone}
      onClose={onClose}
      // The matrix needs the wider dialog; it grows there instead of jumping.
      contentClassName={cn(
        "transition-[max-width] ease-out motion-reduce:transition-none",
        detailsOpen ? "max-w-[min(90vw,56rem)]" : "max-w-[min(90vw,34rem)]",
      )}
      footer={(dismiss) => (
        <div className="flex items-center gap-2">
          <Button
            variant={showNext ? "outline" : "default"}
            className={
              showNext
                ? undefined
                : "bg-attention text-black hover:bg-attention-strong"
            }
            onClick={dismiss}
          >
            {translate("MC_DECL_V2_DONE_BACK_TO_LIST")}
          </Button>
          {showNext && (
            <Button
              className="bg-attention text-black hover:bg-attention-strong"
              onClick={onNext}
            >
              {translate("MC_DECL_V2_DONE_NEXT")}
            </Button>
          )}
        </div>
      )}
    >
      <dl
        data-testid="approved-stats"
        className="grid w-full grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-4 text-center"
      >
        {stats.map((s, i) => (
          <div
            key={s.key}
            className={cn(
              "flex animate-in flex-col items-center gap-0.5 duration-300 fill-mode-both fade-in slide-in-from-bottom-1 motion-reduce:animate-none",
              STAT_DELAYS[i] ?? STAT_DELAYS.at(-1),
            )}
          >
            {/* F30 — the qualifier lives inside `dd`, not as a sibling `span`:
                a `dl`'s child `div` may only contain `dt`/`dd` (plus
                script-supporting elements), and the qualifier is a fact
                about THIS value, so it reads directly under the date it
                qualifies rather than below the label. */}
            <dd className="text-xl text-foreground">
              {s.value}
              {s.qualifier && (
                <span className="mt-0.5 block text-[11px] font-medium text-red1">
                  {s.qualifier}
                </span>
              )}
            </dd>
            <dt className="text-xs tracking-wide text-muted-foreground uppercase">
              {s.label}
            </dt>
          </div>
        ))}
      </dl>

      <div className="flex flex-col items-center gap-1 text-center text-xs text-muted-foreground">
        {/* TUR-1211 part B: a blanket claim over a supplier whose pool is
            empty approves with zero article writes (backend approve guard).
            `articles === 0` is the only signal this dialog has for that case;
            say so, or "0" reads as a failed write and the auto-dismiss hides
            that later articles will not inherit the declaration. The v1
            `ApprovedView` carries the same note. */}
        {result.articles === 0 && (
          <span data-testid="approved-zero-articles">
            {translate("MC_DECL_APPROVED_ZERO_ARTICLES")}
          </span>
        )}
        {result.substanceWrites === 0 && (
          <span>{translate("MC_DECL_V2_APPROVED_NO_SUBSTANCES")}</span>
        )}
        {overwriteLine && <span>{overwriteLine}</span>}
        {manualOverwriteLine && <span>{manualOverwriteLine}</span>}
        <span>
          {translate("MC_DECL_V2_APPROVED_WHEN", {
            when: formatDateTime(result.approvedAt, locale),
          })}
        </span>
      </div>

      {hasDetails && (
        <Collapsible
          open={detailsOpen}
          onOpenChange={handleDetailsOpenChange}
          className="flex w-full flex-col items-center gap-3"
        >
          <ApprovedVerdictSummary snapshot={snapshot} />
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-attention hover:text-attention-strong"
            >
              {translate("MC_DECL_V2_APPROVED_DETAILS")}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  detailsOpen && "rotate-180",
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="w-full overflow-hidden motion-safe:data-[state=closed]:animate-collapsible-up motion-safe:data-[state=open]:animate-collapsible-down">
            <ApprovedVerdictMatrix snapshot={snapshot} />
          </CollapsibleContent>
        </Collapsible>
      )}

      {openWork > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-attention/40 bg-attention/10 px-3 py-2 text-left text-xs text-attention"
        >
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-none" />
          <span>
            {translate("MC_DECL_V2_APPROVED_OPEN_WORK", { n: openWork })}
          </span>
        </div>
      )}
    </AutoDismissDialog>
  )
}

function formatDate(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(d)
}

/** F30 — whole calendar days `iso` is before today, or null when it is today
 *  or in the future. `iso` is a date-only string ("YYYY-MM-DD"), which
 *  `new Date(iso)` already parses as UTC midnight (per the ISO 8601 spec),
 *  so its own UTC calendar-date components are exactly the intended day
 *  regardless of the viewer's timezone; "today" is read the same way so the
 *  two sides of the subtraction are directly comparable. This UTC basis is
 *  not just convenient but REQUIRED for consistency with `formatDate` above:
 *  that function renders the same UTC-midnight instant through `Intl` in the
 *  viewer's LOCAL zone, so a negative-offset viewer already sees a date one
 *  day earlier than the stored value. Reading "today" locally here would
 *  desync the two — a stat tile showing a past date with no qualifier. */
function pastDaysAgo(iso: string): number | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  const todayUtcMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  )
  const diffDays = Math.round((d.getTime() - todayUtcMidnight) / 86_400_000)
  return diffDays < 0 ? Math.abs(diffDays) : null
}

function formatDateTime(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d)
}
